/**
 * Lesson Configuration Builder
 * 드래그 앤 드롭으로 학습 순서를 설정하는 UI
 */
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Save, RotateCcw, Eye } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import type { LessonConfiguration, ComponentConfig, ProductLearningConfig, ModuleLearningConfig, ConfigurationOverrides } from '@/types/configuration'
import { createDefaultConfiguration, isOverridable } from '@/types/configuration'
import { ComponentItem } from './ComponentItem'
import { CompletionRulesEditor } from './CompletionRulesEditor'
import { LearningSettingsEditor } from './LearningSettingsEditor'
import { ProductLearningConfigEditor } from './ProductLearningConfigEditor'
import { ModuleLearningConfigEditor } from './ModuleLearningConfigEditor'

interface ConfigurationBuilderProps {
  initialConfiguration?: LessonConfiguration
  onSave?: (configuration: LessonConfiguration) => void
  onChange?: (configuration: LessonConfiguration) => void
  onCancel?: () => void
  isLoading?: boolean
  showPreview?: boolean
  showHeader?: boolean
  parentOverrides?: ConfigurationOverrides
}

export const ConfigurationBuilder: React.FC<ConfigurationBuilderProps> = ({
  initialConfiguration,
  onSave,
  onChange,
  onCancel,
  isLoading = false,
  showPreview = true,
  showHeader = true,
  parentOverrides
}) => {
  const { t } = useTranslation('configuration')
  // 내부 상태 (uncontrolled 모드에서 사용)
  const [internalConfiguration, setInternalConfiguration] = useState<LessonConfiguration>(
    initialConfiguration || createDefaultConfiguration()
  )
  
  // onChange 모드 (controlled): initialConfiguration 직접 사용
  // onSave 모드 (uncontrolled): 내부 상태 사용
  const isControlled = !!onChange
  const configuration = useMemo(() => {
    return isControlled 
      ? (initialConfiguration || createDefaultConfiguration())
      : internalConfiguration
  }, [isControlled, initialConfiguration, internalConfiguration])
  
  // initialConfiguration이 변경되면 내부 상태도 업데이트 (uncontrolled 모드용)
  useEffect(() => {
    if (!isControlled && initialConfiguration) {
      setInternalConfiguration(initialConfiguration)
    }
  }, [isControlled, initialConfiguration])
  
  // configuration 업데이트 함수
  const setConfiguration = useCallback((updater: LessonConfiguration | ((prev: LessonConfiguration) => LessonConfiguration)) => {
    const currentConfig = isControlled 
      ? (initialConfiguration || createDefaultConfiguration())
      : internalConfiguration
    const newConfig = typeof updater === 'function' ? updater(currentConfig) : updater
    
    if (isControlled && onChange) {
      // controlled 모드: 부모에게 전달
      onChange(newConfig)
    } else {
      // uncontrolled 모드: 내부 상태 업데이트
      setInternalConfiguration(newConfig)
    }
  }, [isControlled, onChange, initialConfiguration, internalConfiguration])
  const [activeTab, setActiveTab] = useState<'sequence' | 'rules' | 'settings' | 'product' | 'module'>('rules')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  // 컴포넌트 업데이트
  const handleUpdateComponent = useCallback((index: number, component: ComponentConfig) => {
    setConfiguration(prev => ({
      ...prev,
      sequence: prev.sequence.map((c, i) => (i === index ? component : c))
    }))
  }, [setConfiguration])

  // 순서 위로 이동
  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return
    setConfiguration(prev => {
      const newSequence = [...prev.sequence]
      ;[newSequence[index - 1], newSequence[index]] = [newSequence[index], newSequence[index - 1]]
      return { ...prev, sequence: newSequence }
    })
  }, [setConfiguration])

  // 순서 아래로 이동
  const handleMoveDown = useCallback((index: number) => {
    setConfiguration(prev => {
      if (index === prev.sequence.length - 1) return prev
      const newSequence = [...prev.sequence]
      ;[newSequence[index], newSequence[index + 1]] = [newSequence[index + 1], newSequence[index]]
      return { ...prev, sequence: newSequence }
    })
  }, [setConfiguration])

  // 드래그 시작
  const handleDragStart = useCallback((_e: React.DragEvent, index: number) => {
    setDragIndex(index)
  }, [])

  // 드래그 오버
  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }, [])

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setConfiguration(prev => {
        const newSequence = [...prev.sequence]
        const [removed] = newSequence.splice(dragIndex, 1)
        newSequence.splice(dragOverIndex, 0, removed)
        return { ...prev, sequence: newSequence }
      })
    }
    setDragIndex(null)
    setDragOverIndex(null)
  }, [dragIndex, dragOverIndex, setConfiguration])

  // 초기화
  const handleReset = () => {
    setConfiguration(initialConfiguration || createDefaultConfiguration())
  }

  // 저장
  const handleSave = () => {
    if (onSave) {
      onSave(configuration)
    }
  }

  return (
    <div className={showHeader ? "bg-white rounded-lg shadow-sm border border-gray-200" : ""}>
      {/* 헤더 */}
      {showHeader && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('builder.title')}</h3>
          <div className="flex items-center gap-2">
            {showPreview && (
              <button
                onClick={() => setShowPreviewModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Eye className="w-4 h-4" />
                {t('builder.preview')}
              </button>
            )}
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t('builder.reset')}
            </button>
          </div>
        </div>
      )}

      {/* 탭 */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('rules')}
          className={`flex-1 min-w-max px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'rules'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('builder.tabRules')}
        </button>
        <button
          onClick={() => setActiveTab('sequence')}
          className={`flex-1 min-w-max px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'sequence'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('builder.tabSequence')}
        </button>
        <button
          onClick={() => setActiveTab('product')}
          className={`flex-1 min-w-max px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'product'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('builder.tabProduct')}
        </button>
        <button
          onClick={() => setActiveTab('module')}
          className={`flex-1 min-w-max px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'module'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('builder.tabModule')}
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 min-w-max px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'settings'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('builder.tabSettings')}
        </button>
      </div>

      {/* 컨텐츠 */}
      <div className="p-6">
        {/* 학습 순서 탭 */}
        {activeTab === 'sequence' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              {isOverridable(parentOverrides, 'sequence', 'orderOverridable')
                ? t('builder.sequenceDragHint')
                : t('builder.sequenceLockedHint')}
            </p>
            {configuration.sequence
              .map((component, originalIndex) => ({ component, originalIndex }))
              .filter(({ component }) => !(component.companyEditable === false && !component.enabled))
              .map(({ component, originalIndex }, displayIndex, filtered) => (
              <ComponentItem
                key={`${component.type}-${component.location || 'default'}-${originalIndex}`}
                component={component}
                index={originalIndex}
                onUpdate={handleUpdateComponent}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                isFirst={displayIndex === 0}
                isLast={displayIndex === filtered.length - 1}
                isDragging={dragIndex === originalIndex}
                dragOverIndex={dragOverIndex}
                sequenceOverrides={parentOverrides?.sequence}
              />
            ))}
          </div>
        )}

        {/* 완료 조건 탭 */}
        {activeTab === 'rules' && (
          <CompletionRulesEditor
            rules={configuration.completionRules}
            onUpdate={(rules) => setConfiguration(prev => ({ ...prev, completionRules: rules }))}
            parentOverrides={parentOverrides?.completionRules}
          />
        )}

        {/* 학습 설정 탭 */}
        {activeTab === 'settings' && (
          <LearningSettingsEditor
            settings={configuration.learningSettings}
            onUpdate={(settings) => setConfiguration(prev => ({ ...prev, learningSettings: settings }))}
            parentOverrides={parentOverrides?.learningSettings}
          />
        )}

        {/* 상품 레벨 학습 탭 */}
        {activeTab === 'product' && (
          <ProductLearningConfigEditor
            config={configuration.productLearning}
            onUpdate={(productLearning) => setConfiguration(prev => ({ ...prev, productLearning }))}
            readOnly={parentOverrides?.productLearning === false}
          />
        )}

        {/* 모듈 레벨 학습 탭 */}
        {activeTab === 'module' && (
          <ModuleLearningConfigEditor
            config={configuration.moduleLearning}
            onUpdate={(moduleLearning) => setConfiguration(prev => ({ ...prev, moduleLearning }))}
            readOnly={parentOverrides?.moduleLearning === false}
          />
        )}
      </div>

      {/* 푸터 - onSave가 있을 때만 표시 */}
      {onSave && (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('builder.cancel')}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isLoading ? t('builder.saving') : t('builder.save')}
          </button>
        </div>
      )}

      {/* 미리보기 모달 */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('builder.previewTitle')}</DialogTitle>
        </DialogHeader>
        <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
          {JSON.stringify(configuration, null, 2)}
        </pre>
        <DialogFooter>
          <button
            onClick={() => setShowPreviewModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('builder.close')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </div>
  )
}
